import React, {useEffect, useRef, useState} from 'react';

import {makeBorderClass} from 'components/Form/Input';

import DoneButton from './Controls/DoneButton';
import FloatingSlider from './Controls/FloatingSlider';
import UploadButton from './Controls/UploadButton';

import Cropper from 'react-cropper';
import 'cropperjs/dist/cropper.css';
import ErrorMessage from 'components/Message/ErrorMessage';
import i18n from 'i18next';

import {FILE_UPLOAD_LIMIT_MB} from './constants';
import {downloadImage} from '../../utils';

type Props = {
  id?: string;
  imageId?: string;
  uploadedFile?: File;
  onChange: (newFile: File) => void;
  disabled?: boolean;
  errorMessage?: string;
};

export default function ImageUploaderField(props: Props) {
  // General purpose state
  const [error, setError] = useState<string>();
  // The current image *data* (created with URL.createObjectURL)
  const [currentImageData, setCurrentImageData] = useState<string>('');

  // Refs
  const fileInputRef = useRef<HTMLInputElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const cropperRef = useRef<any>(null);

  // File cropping state.
  const [croppingFile, setCroppingFile] = useState<File>();
  const [cropping, setCropping] = useState(false);

  // Get/download current image data
  useEffect(() => {
    if (currentImageData) {
      URL.revokeObjectURL(currentImageData);
    }

    if (croppingFile) {
      setCurrentImageData(URL.createObjectURL(croppingFile));
    } else if (props.uploadedFile) {
      setCurrentImageData(URL.createObjectURL(props.uploadedFile));
    } else if (props.imageId) {
      downloadImage(props.imageId).then(d => setCurrentImageData(d || ''));
    } else {
      setCurrentImageData('');
    }

    // Cleanup when we unmount this component
    return () => URL.revokeObjectURL(currentImageData);

    // Don't depend on currentImageData (avoid infinite loop)
    // eslint-disable-next-line
  }, [props.imageId, croppingFile, props.uploadedFile]);

  /** Present the file picker. */
  const pickFile = () => {
    fileInputRef.current?.click();
  };

  /** Get first file from the file input and set it to local state. */
  const saveLocalFile = () => {
    setError(undefined);

    const files = fileInputRef.current?.files || [];
    if (files.length > 0) {
      const file = files[0];
      const filesizeMb = file.size / 1024 / 1024;

      if (filesizeMb > FILE_UPLOAD_LIMIT_MB) {
        setError(i18n.t('imageUploader.imageTooBig'));
        return;
      }

      setCroppingFile(file);
      setCropping(true);
    }

    // Clear the file input value so that it's called
    // next time if the same file selected.
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  /** Invisible element for file uploading. */
  const fileUploaderElement = (
    <input
      type="file"
      className="hidden"
      onChange={saveLocalFile}
      accept="image/png,image/x-png,image/jpeg,image/jpg"
      ref={fileInputRef}
    />
  );

  /** Done Cropping */
  const handleDoneCropping = () => {
    const cropper = cropperRef.current?.cropper as Cropper | undefined;

    if (cropper) {
      const canvas = cropper.getCroppedCanvas({
        maxWidth: 1000,
        maxHeight: 750,
      }) as HTMLCanvasElement;

      canvas.toBlob(blob => {
        if (blob) {
          const file = new File([blob], 'new_thumb', {type: blob.type});
          props.onChange(file);
        }
      });

      setCroppingFile(undefined);
      setCropping(false);
    }
  };

  /** Preview the uploaded image or show message about uploading the image */
  const gotImage = props.imageId || props.uploadedFile;
  const uploadedImagePreview = gotImage ? (
    <img
      src={currentImageData}
      alt=" "
      className={`${makeBorderClass()} w-full`}
    />
  ) : (
    <div
      className={`p-5 ${makeBorderClass()} cursor-pointer`}
      onClick={pickFile}
    >
      <ul className="list-disc pl-6 w-11/12">
        <li>{i18n.t('fileInput.info')}</li>
        <li>{i18n.t('fileInput.sizeInfo')}</li>
        <li>{i18n.t('fileInput.formatInfo')}</li>
      </ul>
    </div>
  );

  // Handle zoom in
  const handleZoomIncrease = () => {
    const cropper = cropperRef.current?.cropper as Cropper | undefined;
    cropper && cropper.zoom(0.1);
  };

  // Handle zoom out
  const handleZoomDecrease = () => {
    const cropper = cropperRef.current?.cropper as Cropper | undefined;
    cropper && cropper.zoom(-0.1);
  };

  return (
    <>
      {/* Potential error bubbles */}
      {error && <ErrorMessage className="my-2">{error}</ErrorMessage>}
      {props.errorMessage && (
        <ErrorMessage className="my-2">{props.errorMessage}</ErrorMessage>
      )}

      {/* Cropper/preview */}

      <div className="relative rounded-large overflow-hidden">
        {!props.disabled && fileUploaderElement}

        {/* If cropping - display cropper element, otherwise show preview of the cropped image.*/}
        {cropping && !props.disabled ? (
          <div style={{width: '100%', paddingTop: '75%'}}>
            <Cropper
              src={currentImageData}
              className="absolute left-0 top-0 right-0 bottom-0"
              // Cropper.js options
              initialAspectRatio={4 / 3}
              viewMode={3}
              dragMode="move"
              autoCropArea={1}
              restore={false}
              modal={false}
              guides={false}
              highlight={false}
              cropBoxMovable={false}
              cropBoxResizable={false}
              toggleDragModeOnDblclick={false}
              zoomable={true}
              zoomOnTouch={false}
              zoomOnWheel={true}
              ref={cropperRef}
            />
          </div>
        ) : (
          uploadedImagePreview
        )}

        {/* Show zoom slider if cropping */}
        {cropping && !props.disabled && (
          <FloatingSlider inc={handleZoomIncrease} dec={handleZoomDecrease} />
        )}

        {/* Show done button if cropping */}
        {cropping && !props.disabled && (
          <DoneButton onClick={handleDoneCropping} />
        )}

        {/* Show upload button if not disabled */}
        {!props.disabled && <UploadButton onClick={pickFile} />}
      </div>
    </>
  );
}
